/**
 * Azure Monitor Application Insights -- Telemetry Service
 *
 * Singleton wrapper around the AppInsights SDK.
 * All tracking calls are fire-and-forget; they never throw.
 *
 * In local dev without a connection string, all calls become no-ops.
 */
import { ApplicationInsights, type IEventTelemetry } from "@microsoft/applicationinsights-web";

// ── Connection ───────────────────────────────────────────────────────────────

const CONNECTION_STRING = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING ?? "";

const isEnabled = CONNECTION_STRING.length > 0;

// ── SDK Initialisation ───────────────────────────────────────────────────────

let appInsights: ApplicationInsights | null = null;

if (isEnabled) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString: CONNECTION_STRING,
      enableAutoRouteTracking: false,
      disableAjaxTracking: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableExceptionTracking: false,
      enableUnhandledPromiseRejectionTracking: true,
      samplingPercentage: 100,
    },
  });
  appInsights.loadAppInsights();
} else if (import.meta.env.DEV) {
  console.debug(
    "[telemetry] No VITE_APPINSIGHTS_CONNECTION_STRING set -- telemetry disabled. " +
      "All tracking calls are no-ops."
  );
}

// ── Types ────────────────────────────────────────────────────────────────────

export type EventProperties = Record<string, string | number | boolean | undefined>;
export type EventMeasurements = Record<string, number>;

// ── Public API ───────────────────────────────────────────────────────────────

export function trackEvent(
  name: string,
  properties?: EventProperties,
  measurements?: EventMeasurements
): void {
  try {
    if (!appInsights) return;
    const payload: IEventTelemetry = { name };
    if (properties) payload.properties = properties;
    if (measurements) payload.measurements = measurements;
    appInsights.trackEvent(payload);
  } catch {
    // Telemetry must never break the application
  }
}

export function trackPageView(name: string, url?: string, properties?: EventProperties): void {
  try {
    appInsights?.trackPageView({ name, uri: url, properties });
  } catch {
    // silent
  }
}

export function trackException(error: unknown, properties?: EventProperties): void {
  try {
    if (!appInsights) return;
    const exception = error instanceof Error ? error : new Error(String(error));
    appInsights.trackException({ exception, properties });
  } catch {
    // silent
  }
}

export function setAuthenticatedUser(userId: string, tenantId?: string): void {
  try {
    appInsights?.setAuthenticatedUserContext(userId, tenantId, true);
  } catch {
    // silent
  }
}

export function clearAuthenticatedUser(): void {
  try {
    appInsights?.clearAuthenticatedUserContext();
  } catch {
    // silent
  }
}

export function startTimer(): () => number {
  const start = performance.now();
  return () => Math.round(performance.now() - start);
}

export { appInsights };
